/**
 * AoeZone.ts
 *
 * Represents a temporary area of effect damage zone.
 * Damages enemies that enter or stay within the zone while active.
 * Includes debug visualization.
 */

import Phaser from 'phaser';
import { Unit } from '../entities/base/Unit';
import { Entity } from '../entities/base/Entity';
import { Allegiance } from '../weapons/DataManagerWeapon';
import { LayerDepths } from '../constants/enums';

export interface AoeZoneConfig {
  x: number;
  y: number;
  radius: number;
  damage: number;
  duration: number;
  allegiance: Allegiance;
  tint?: number;
  sourceTarget?: Entity;  // Entity that was directly hit (excluded from first tick)
  attackType?: string;    // Attack type for weakTo immunity check
}

/**
 * AoeZone - Temporary damage area
 *
 * Features:
 * - Visual debug circle showing the AOE radius
 * - Damages enemies within radius every tick
 * - Tracks which enemies have been damaged to avoid multi-hit
 * - Auto-destroys after duration
 */
export class AoeZone extends Phaser.GameObjects.GameObject {
  private config: AoeZoneConfig;
  private debugCircle: Phaser.GameObjects.Graphics;
  private startTime: number;
  private damagedEntities: Set<Entity> = new Set();
  private enemyGroup?: Phaser.GameObjects.Group;
  public x: number;
  public y: number;

  constructor(scene: Phaser.Scene, config: AoeZoneConfig) {
    super(scene, 'AoeZone');
    this.config = config;
    this.x = config.x;
    this.y = config.y;
    this.startTime = scene.time.now;

    // Add source target to damaged set (already took direct hit damage)
    if (config.sourceTarget) {
      this.damagedEntities.add(config.sourceTarget);
    }

    // Create debug circle graphics directly on scene
    this.debugCircle = scene.add.graphics();
    this.debugCircle.setDepth(LayerDepths.AoeDebug);
    this.drawDebugCircle(1.0);
    this.debugCircle.setData('effect', true);

    // Add to scene display list
    scene.add.existing(this);
  }

  /**
   * Set the enemy group to check for collisions
   */
  setEnemyGroup(group: Phaser.GameObjects.Group): void {
    this.enemyGroup = group;
  }

  /**
   * Draw the debug circle at world position
   */
  private drawDebugCircle(alpha: number): void {
    this.debugCircle.clear();

    const color = this.config.tint ?? 0x00aaff;
    const cx = this.x;
    const cy = this.y;
    const radius = this.config.radius;

    // Outer circle (stroke) - thick and visible
    this.debugCircle.lineStyle(4, color, alpha);
    this.debugCircle.strokeCircle(cx, cy, radius);

    // Inner fill (semi-transparent)
    this.debugCircle.fillStyle(color, alpha * 0.2);
    this.debugCircle.fillCircle(cx, cy, radius);

    // Inner pulse circle
    this.debugCircle.lineStyle(2, 0xffffff, alpha * 0.6);
    this.debugCircle.strokeCircle(cx, cy, radius * 0.5);

    // Center cross for reference
    this.debugCircle.lineStyle(2, 0xffffff, alpha);
    this.debugCircle.lineBetween(cx - 10, cy, cx + 10, cy);
    this.debugCircle.lineBetween(cx, cy - 10, cx, cy + 10);
  }

  /**
   * Update - check for enemies and apply damage
   */
  update(time: number, _delta: number): void {
    const elapsed = time - this.startTime;
    const progress = elapsed / this.config.duration;

    // Check if expired
    if (progress >= 1) {
      this.destroy();
      return;
    }

    // Update visual (fade out over time)
    const alpha = 1 - (progress * 0.7);  // Fade from 1.0 to 0.3
    this.drawDebugCircle(alpha);

    // Check for enemies in range
    this.checkEnemiesInRange();
  }

  /**
   * Check and damage enemies within range using Phaser collision
   * Uses circle-to-rectangle intersection for accurate hitbox detection
   */
  private checkEnemiesInRange(): void {
    if (!this.enemyGroup) return;

    const entities = this.enemyGroup.getChildren() as Unit[];

    // Create AOE circle geometry
    const aoeCircle = new Phaser.Geom.Circle(this.x, this.y, this.config.radius);

    for (const entity of entities) {
      if (!entity.active) continue;

      // Skip already damaged entities
      if (this.damagedEntities.has(entity)) continue;

      // Get entity's physics body bounds
      const body = entity.body as Phaser.Physics.Arcade.Body;
      if (!body) continue;

      // Create rectangle from entity's body bounds
      const entityRect = new Phaser.Geom.Rectangle(
        body.x,
        body.y,
        body.width,
        body.height
      );

      // Check circle-to-rectangle intersection
      if (Phaser.Geom.Intersects.CircleToRectangle(aoeCircle, entityRect)) {
        // Check if entity has damage type restriction (weakTo)
        const weakTo = entity.config?.weakTo as string | undefined;
        if (weakTo && this.config.attackType !== weakTo) {
          // Entity is immune to this attack type - mark as processed but don't damage
          this.damagedEntities.add(entity);
          continue;
        }

        // Apply damage
        if (typeof entity.takeDamage === 'function') {
          entity.takeDamage(this.config.damage);
          this.damagedEntities.add(entity);
        }
      }
    }
  }

  /**
   * Get remaining duration
   */
  getRemainingTime(): number {
    const elapsed = this.scene.time.now - this.startTime;
    return Math.max(0, this.config.duration - elapsed);
  }

  /**
   * Cleanup
   */
  destroy(fromScene?: boolean): void {
    if (this.debugCircle) {
      this.debugCircle.clear();
      this.debugCircle.setVisible(false);
      if (this.scene?.children) {
        this.scene.children.remove(this.debugCircle);
      }
      this.debugCircle.destroy(true);
      (this as { debugCircle: Phaser.GameObjects.Graphics | null }).debugCircle = null;
    }
    this.damagedEntities.clear();
    super.destroy(fromScene);
  }
}
